import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Modal, Field, TextInput, SelectInput, Button } from './ui'
import { setEventState, getEvents } from '../slices/EventSlice';

const today = new Date().toISOString().substr(0, 10);

/*
 * EventDownload — "Export Events" filter dialog (opened by the Export CSV button on the
 * Event Monitor). Presentation is the design-system Modal + Field/Input controls; the
 * filter keys and the download action (getEvents({ download:true, filters })) are
 * unchanged from the original inline-styled version, so the export behaves identically.
 */
const EventDownload = (props) => {
  const [filters, setFilters] = useState({})

  useEffect(() => {
    // eventFilters is an array of {id, value}; flatten to a name→value object and lift the
    // timestamp range out to sDate/eDate (verbatim from the original component).
    let params = Object.fromEntries(props.eventFilters.map(obj => [obj.id, obj.value]))
    params.sDate = params.timestamp?.sDate || ''
    params.eDate = params.timestamp?.eDate || ''
    setFilters(params)
  // eslint-disable-next-line
  }, [])

  const setFilterData = (field, value) => setFilters((f) => ({ ...f, [field]: value }))

  const handleExport = async () => {
    if (Object.values(filters).filter(f => f).length === 0) {
      await props.setEventState({ field: 'eventDownloadError', value: 'Due to the volume of available data, please select at least one filter.' })
    } else {
      props.toggleDownloadModal()
      await props.setEventState({ field: 'eventDownloadStatus', value: 'downloading' })
      await props.getEvents({ download: true, filters: filters })
    }
  }

  const isAdmin = props.authId === 1 || props.authId === 100
  const full = { gridColumn: '1 / -1' }   // span both columns of the grid below

  return (
    <Modal
      open
      onClose={props.toggleDownloadModal}
      title="Export Events"
      subtitle="Filter the crossings to export, then download a CSV."
      size="md"
      footer={
        <div className="ui-modal__actions">
          <Button variant="default" onClick={() => setFilters({})}>Clear</Button>
          <Button variant="primary" icon={<span aria-hidden>↓</span>} onClick={handleExport}>Export CSV</Button>
        </div>
      }
    >
      {props.eventDownloadError && (
        <div className="login-error" style={{ marginBottom: 16 }}>{props.eventDownloadError}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
        <Field label="Start date">
          <TextInput type="date" value={filters.sDate || ''} max={today} onChange={e => setFilterData('sDate', e.target.value)} />
        </Field>
        <Field label="End date">
          <TextInput type="date" value={filters.eDate || ''} max={today} onChange={e => setFilterData('eDate', e.target.value)} />
        </Field>

        <Field label="Type">
          <SelectInput value={filters.typeId || ''} onChange={e => setFilterData('typeId', e.target.value)}>
            <option value=''>All types</option>
            <option value='1'>IN</option>
            <option value='2'>OUT</option>
            <option value='3'>DENIED</option>
            <option value='4'>ACCIDENT</option>
            {isAdmin && <option disabled>──────────</option>}
            {isAdmin && <option value='100'>LOGIN</option>}
            {isAdmin && <option value='101'>LOGOUT</option>}
          </SelectInput>
        </Field>
        <Field label="Gate">
          <SelectInput value={filters.gateId || ''} onChange={e => setFilterData('gateId', e.target.value)}>
            <option value=''>All gates</option>
            {props.gates.map(row => <option key={row.id} value={row.id}>{row.sName}</option>)}
          </SelectInput>
        </Field>

        {/* Super-only: scope the export to one subscriber (tenant). Placed before Customer to match
            the feed's broad→narrow order; only supers are unscoped server-side so it's a no-op UI for
            anyone else (and the getEvents download path preserves a super's subscriberId). */}
        {props.authId === 100 &&
          <Field label="Subscriber">
            <SelectInput value={filters.subscriberId || ''} onChange={e => setFilterData('subscriberId', e.target.value)}>
              <option value=''>All subscribers</option>
              {props.subscribers.map(row => <option key={row.id} value={row.id}>{row.sName}</option>)}
            </SelectInput>
          </Field>
        }
        <Field label="Customer">
          <SelectInput value={filters.customerId || ''} onChange={e => setFilterData('customerId', e.target.value)}>
            <option value=''>All customers</option>
            {props.customers.map(row => <option key={row.id} value={row.id}>{row.sName}</option>)}
          </SelectInput>
        </Field>
        <Field label="Logged by">
          <SelectInput value={filters.guardId || ''} onChange={e => setFilterData('guardId', e.target.value)}>
            <option value=''>Anyone</option>
            {props.guards.map(row => <option key={row.id} value={row.id}>{row.sFullName}</option>)}
          </SelectInput>
        </Field>

        <Field label="Company">
          <TextInput value={filters.company || ''} placeholder="Any company" onChange={e => setFilterData('company', e.target.value)} />
        </Field>
        <Field label="Driver">
          <TextInput value={filters.driver || ''} placeholder="Any driver" onChange={e => setFilterData('driver', e.target.value)} />
        </Field>
        <Field label="License plate">
          <TextInput value={filters.lpn || ''} placeholder="Any plate" onChange={e => setFilterData('lpn', e.target.value)} />
        </Field>

        <div style={full}>
          <Field label="Comment">
            <TextInput value={filters.comment || ''} placeholder="Any text in the comment" onChange={e => setFilterData('comment', e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

const mapStateToProps = (state) => {
  return {
    authId: state.auth.authId,
    customers: state.customer.customers,
    gates: state.gate.gates,
    guards: state.user.guards,
    subscribers: state.subscriber.subscribers || [],
    eventFilters: state.event.eventFilters,
    eventDownloadError: state.event.eventDownloadError,
  }
}
export default connect(mapStateToProps, { setEventState, getEvents })(EventDownload)
